<template>
    <div class="ix-dashboard-block bordered">
        <div class="d-flex align-items-end flex-wrap mb-3" style="gap: 12px;">
            <div class="form-group mb-0" style="min-width: 190px;">
                <label for="movement-totals-preset" class="mb-1 d-block text-muted" style="font-size: 12px;">Période</label>
                <select id="movement-totals-preset" class="form-control" v-model="selectedPreset" @change="onPresetChange">
                    <option v-for="preset in presets" :key="preset.key" :value="preset.key">{{ preset.label }}</option>
                    <option value="custom">Personnalisée</option>
                </select>
            </div>
            <div class="form-group mb-0">
                <label for="movement-totals-start" class="mb-1 d-block text-muted" style="font-size: 12px;">Du</label>
                <input type="date" id="movement-totals-start" class="form-control" style="width: auto;" v-model="startAt" @change="onDateChange">
            </div>
            <div class="form-group mb-0">
                <label for="movement-totals-end" class="mb-1 d-block text-muted" style="font-size: 12px;">Au</label>
                <input type="date" id="movement-totals-end" class="form-control" style="width: auto;" v-model="endAt" @change="onDateChange">
            </div>
        </div>

        <div class="row">
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-count-box-title">Total des entrées de stock</h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatCurrency(totals.in) }}</span>
                </div>
            </div>
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-count-box-title">Total des sorties de stock</h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatCurrency(totals.out) }}</span>
                </div>
            </div>
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-count-box-title">
                        Valeur du stock
                        <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Valeur du stock tel qu'il est aujourd'hui. Contrairement aux entrées et aux sorties, cet indicateur est une photographie de l'instant."></i>
                    </h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatCurrency(totals.stock_value) }}</span>
                    <span class="ix-count-box-subvalue">à aujourd'hui, hors période</span>
                </div>
            </div>
        </div>

        <div class="row mt-3">
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-count-box-title">
                        Délai d'appro moyen
                        <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Délai moyen entre l'émission de la commande fournisseur et sa réception, sur les réceptions de la période."></i>
                    </h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatDays(kpi.average_supply_delay_days) }}</span>
                </div>
            </div>
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-count-box-title">
                        Taux de rotation
                        <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Quantité en stock en début de période rapportée au stock moyen de la période (moyenne du stock en début et en fin de période)."></i>
                    </h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatRate(kpi.rotation_rate) }}</span>
                </div>
            </div>
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-count-box-title">
                        Durée moyenne de stock
                        <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="365 / taux de rotation."></i>
                    </h2>
                    <span class="ix-count-box-value">{{ loading ? '-' : formatDays(kpi.average_stock_duration_days) }}</span>
                </div>
            </div>
        </div>

        <p class="text-muted mt-2 mb-0" style="font-size: 12px;">
            Entrées et sorties des mouvements de stock &middot; {{ period.label }}
        </p>
    </div>
</template>

<script>
import $ from "jquery";
import { buildPeriodPresets } from "../../periods";

export default {
    props: {
        routeMovementTotals: String,
    },
    data() {
        return {
            selectedPreset: 'year',
            startAt: '',
            endAt: '',
            loading: false,
            period: { label: '' },
            totals: { in: 0, out: 0, stock_value: 0 },
            kpi: { average_supply_delay_days: null, rotation_rate: null, average_stock_duration_days: null },
        }
    },
    computed: {
        presets() {
            return buildPeriodPresets();
        },
    },
    methods: {
        formatCurrency(value) {
            if (value === null || value === undefined) return '-,-€';

            return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
        },
        formatDays(value) {
            if (value === null || value === undefined) return '-';

            return Number(value).toLocaleString('fr-FR') + ' j';
        },
        formatRate(value) {
            if (value === null || value === undefined) return '-';

            return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        onPresetChange() {
            if (this.selectedPreset === 'custom') {
                return;
            }

            const preset = this.presets.find((p) => p.key === this.selectedPreset);

            if (!preset) {
                return;
            }

            this.startAt = preset.start;
            this.endAt = preset.end;
            this.fetchTotals();
        },
        onDateChange() {
            // Une saisie manuelle de date sort forcément d'un preset.
            this.selectedPreset = 'custom';
            this.fetchTotals();
        },
        async fetchTotals() {
            if (this.startAt && this.endAt && this.startAt > this.endAt) {
                return;
            }

            this.loading = true;

            const params = new URLSearchParams();
            if (this.startAt) params.append('period_start', this.startAt);
            if (this.endAt) params.append('period_end', this.endAt);

            await fetch(this.routeMovementTotals + '?' + params.toString(), {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                return response.json();
            }).then((data) => {
                this.totals = data.totals;
                this.kpi = data.kpi;
                this.period = data.period;
            }).catch(() => {
                this.totals = { in: 0, out: 0, stock_value: 0 };
                this.kpi = { average_supply_delay_days: null, rotation_rate: null, average_stock_duration_days: null };
            });

            this.loading = false;
            this.$nextTick(() => {
                $('[data-toggle="tooltip"]:not(.copy-button)').tooltip();
            });
        },
    },
    mounted() {
        const preset = this.presets.find((p) => p.key === this.selectedPreset);

        if (preset) {
            this.startAt = preset.start;
            this.endAt = preset.end;
        }

        this.fetchTotals();
    }
}
</script>
